import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Drawer, Form, Input, Select, Space, Tag, Popconfirm, Typography, message, Alert, Collapse } from 'antd';
import { getConfigs, upsertConfig, deleteConfig } from '../api/config';
import { authorize } from '../api/auth';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PLATFORM_OPTIONS = [
  { label: 'Facebook', value: 'facebook' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'X', value: 'x' },
];

const DEFAULT_SCOPES = {
  facebook: ['pages_manage_posts', 'pages_read_engagement', 'pages_show_list'],
  instagram: ['instagram_basic', 'instagram_content_publish'],
  linkedin: ['w_member_social', 'r_liteprofile', 'r_emailaddress'],
  x: ['tweet.read', 'tweet.write', 'users.read']
};

const SETUP_INSTRUCTIONS = {
  facebook: {
    title: "Facebook Setup Instructions",
    steps: [
      "1. Go to https://developers.facebook.com/",
      "2. Create a new app (Business type)",
      "3. Add 'Facebook Login' product",
      "4. Copy App ID as Client ID",
      "5. Copy App Secret as Client Secret",
      "6. Add redirect URI in Facebook Login settings"
    ]
  },
  instagram: {
    title: "Instagram Setup Instructions", 
    steps: [
      "1. Use the same Facebook App as above",
      "2. Add 'Instagram Basic Display' product",
      "3. Use same App ID and App Secret",
      "4. Configure redirect URI in Instagram Basic Display",
      "5. Submit for app review for production use"
    ]
  },
  linkedin: {
    title: "LinkedIn Setup Instructions",
    steps: [
      "1. Go to https://www.linkedin.com/developers/",
      "2. Create a new app",
      "3. Add 'Sign In with LinkedIn' product",
      "4. Copy Client ID and Client Secret",
      "5. Add redirect URI in Auth settings"
    ]
  },
  x: {
    title: "X (Twitter) Setup Instructions",
    steps: [
      "1. Go to https://developer.twitter.com/",
      "2. Create a new app in Developer Portal",
      "3. Enable OAuth 2.0 with PKCE",
      "4. Copy Client ID and Client Secret", 
      "5. Add redirect URI in app settings"
    ]
  }
};

const ProviderSettingsPage = () => {
  const { canViewCreateModule, canEditModule, can } = usePermissions();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [lastError, setLastError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLastError(null);
    try {
      const res = await getConfigs();
      console.log('Loaded configs:', res);
      setData(res?.data || []);
    } catch (e) {
      console.error('Load error:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to load configs';
      setLastError(`Load failed: ${errorMessage} (Status: ${e?.response?.status})`);
      messageApi.error(`Load failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    load();
    
    // Check for OAuth callback results in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const platform = urlParams.get('platform');
    const status = urlParams.get('status'); // e.g., code_received
    const tokenSaved = urlParams.get('tokenSaved'); // 'true' | 'false'
    
    if (success && platform) {
      if (tokenSaved === 'false' || status === 'code_received') {
        messageApi.warning(`Connected to ${platform}. Authorization code received; token not saved yet.`);
      } else {
        messageApi.success(`Successfully connected to ${platform}`);
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      messageApi.error(`Connection failed: ${error}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const onAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const onPlatformChange = (platform) => {
    // Auto-fill default scopes for the selected platform
    const defaultScopes = DEFAULT_SCOPES[platform] || [];
    form.setFieldsValue({ scopes: defaultScopes });
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      platform: record.platform,
      clientId: record.clientId,
      redirectUri: record.redirectUri,
      scopes: record.scopes,
      clientSecret: '', // keep empty; user can set to replace
    });
    setOpen(true);
  };

  const onDelete = async (record) => {
    try {
      await deleteConfig(record._id);
      messageApi.success('Deleted');
      load();
    } catch (e) {
      messageApi.error('Delete failed');
    }
  };

  const onFinish = async (values) => {
    setLastError(null);
    try {
      console.log('Submitting values:', values);
      
      // Check required fields on frontend
      if (!values.platform || !values.clientId || !values.redirectUri) {
        messageApi.error('Please fill in all required fields');
        return;
      }
      
      if (!editing && !values.clientSecret) {
        messageApi.error('Client Secret is required for new providers');
        return;
      }
      
      const result = await upsertConfig(values);
      console.log('Save result:', result);
      messageApi.success(editing ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e) {
      console.error('Save error:', e);
      const errorDetails = {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
        url: e?.config?.url,
        method: e?.config?.method
      };
      console.log('Full error details:', errorDetails);
      
      const errorMessage = e?.response?.data?.message || e?.message || 'Save failed';
      setLastError(`Save failed: ${errorMessage} (Status: ${e?.response?.status})`);
      messageApi.error(`Save failed: ${errorMessage}`);
    }
  };

  const onAuthorize = async (record) => {
    try {
      console.log('Starting OAuth for platform:', record.platform);
      console.log('Provider config:', record);
      
      const res = await authorize(record.platform);
      console.log('OAuth response:', res);
      
      const url = res?.data?.url;
      if (url) {
        console.log('Opening OAuth URL:', url);
        window.open(url, '_blank');
      } else {
        console.error('No OAuth URL in response:', res);
        messageApi.error('Authorize URL not available');
      }
    } catch (e) {
      console.error('OAuth error:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to initiate OAuth';
      const errorDetails = {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
        url: e?.config?.url,
        method: e?.config?.method
      };
      console.log('Full OAuth error details:', errorDetails);
      messageApi.error(`OAuth failed: ${errorMessage}`);
    }
  };

  const testConnection = async () => {
    try {
      console.log('Testing API connection...');
      const baseURL = process.env.REACT_APP_BASE_URL || 'http://localhost:5050'; // Changed from 6050 to 5050
      const token = localStorage.getItem('token');
      console.log('API Base URL:', baseURL);
      console.log('Token exists:', !!token);
      
      const response = await fetch(`${baseURL}/api/v1/smm/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });
      
      console.log('Response status:', response.status);
      const data = await response.text();
      console.log('Response data:', data);
      
      if (response.ok) {
        messageApi.success('API connection successful');
      } else {
        messageApi.error(`API connection failed: ${response.status} ${response.statusText}`);
      }
    } catch (e) {
      console.error('Connection test error:', e);
      messageApi.error(`Connection test failed: ${e.message}`);
    }
  };

  const testOAuthEndpoint = async (platform = 'facebook') => {
    try {
      console.log(`Testing OAuth endpoint for ${platform}...`);
      const baseURL = process.env.REACT_APP_BASE_URL || 'http://localhost:5050';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${baseURL}/api/v1/smm/auth/${platform}/authorize`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include'
      });
      
      console.log('OAuth endpoint response status:', response.status);
      const data = await response.text();
      console.log('OAuth endpoint response data:', data);
      
      if (response.ok) {
        messageApi.success(`OAuth endpoint for ${platform} is working`);
      } else {
        messageApi.error(`OAuth endpoint failed: ${response.status} ${response.statusText}`);
      }
    } catch (e) {
      console.error('OAuth endpoint test error:', e);
      messageApi.error(`OAuth test failed: ${e.message}`);
    }
  };

  const copyRedirect = () => {
    const backendBase = process.env.REACT_APP_BASE_URL || 'http://localhost:5050';
    const platform = form.getFieldValue('platform') || '{platform}';
    const uri = `${backendBase}/api/v1/smm/auth/${platform}/callback`;
    navigator.clipboard.writeText(uri);
    messageApi.success('Redirect URI copied');
  };

  const columns = useMemo(() => [
    { title: 'Platform', dataIndex: 'platform', key: 'platform', render: (v) => <Tag>{v}</Tag> },
    { title: 'Client ID', dataIndex: 'clientId', key: 'clientId' },
    { title: 'Redirect URI', dataIndex: 'redirectUri', key: 'redirectUri' },
    { title: 'Scopes', dataIndex: 'scopes', key: 'scopes', render: (s) => (s || []).join(', ') },
    { title: 'Secret', key: 'secret', render: (_, r) => r.hasClientSecret ? <Tag color="green">Set</Tag> : <Tag color="red">Not Set</Tag> },
    {
      title: 'Actions', key: 'actions', render: (_, record) => (
        <Space>
          {canEditModule('smm') && <Button size="small" onClick={() => onEdit(record)}>Edit</Button>}
          <Button size="small" onClick={() => onAuthorize(record)}>Connect</Button>
          {can('smm', 'delete') && (
            <Popconfirm title="Delete config?" onConfirm={() => onDelete(record)}>
              <Button size="small" danger>Delete</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ], [data]);

  return (
    <div className="container-fluid" style={{ padding: 24 }}>
      {contextHolder}
      
      {lastError && (
        <Alert 
          message="Debug Information" 
          description={lastError} 
          type="error" 
          showIcon 
          closable 
          onClose={() => setLastError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      
      <Card title={<Space><Title level={4} style={{ margin: 0 }}>SMM Provider Settings</Title><Text type="secondary">Manage multiple provider credentials</Text></Space>} extra={<Space><Button onClick={testConnection}>Test API</Button><Button onClick={() => testOAuthEndpoint('facebook')}>Test OAuth</Button>{canViewCreateModule('smm') && <Button type="primary" onClick={onAdd}>Add Provider</Button>}</Space>}>
        <Table rowKey="_id" dataSource={data} columns={columns} loading={loading} pagination={false} />
      </Card>

      <Drawer
        title={editing ? 'Edit Provider' : 'Add Provider'}
        open={open}
        onClose={() => setOpen(false)}
        width={520}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item label="Platform" name="platform" rules={[{ required: true }]}>
            <Select 
              options={PLATFORM_OPTIONS} 
              placeholder="Select platform" 
              disabled={!!editing}
              onChange={onPlatformChange}
            />
          </Form.Item>
          
          {form.getFieldValue('platform') && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Collapse ghost>
                <Panel header={`📖 ${SETUP_INSTRUCTIONS[form.getFieldValue('platform')]?.title}`} key="1">
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    {SETUP_INSTRUCTIONS[form.getFieldValue('platform')]?.steps.map((step, idx) => (
                      <li key={idx} style={{ marginBottom: 4 }}>
                        <Text type="secondary">{step}</Text>
                      </li>
                    ))}
                  </ol>
                </Panel>
              </Collapse>
            </Card>
          )}
          <Form.Item label="Client ID" name="clientId" rules={[{ required: true }]}>
            <Input placeholder="Enter Client ID" />
          </Form.Item>
          <Form.Item label="Client Secret" name="clientSecret" tooltip={editing ? 'Leave blank to keep existing secret' : undefined} rules={editing ? [] : [{ required: true }]}>
            <Input.Password placeholder={editing ? '•••••• (leave blank to keep existing)' : 'Enter Client Secret'} />
          </Form.Item>
          <Space style={{ marginBottom: 12 }}>
            <Button onClick={copyRedirect}>Copy Redirect URI</Button>
          </Space>
          <Form.Item label="Redirect URI" name="redirectUri" rules={[{ required: true }]}>
            <Input placeholder="https://your.app.com/api/v1/smm/auth/{platform}/callback" />
          </Form.Item>
          <Form.Item label="Scopes" name="scopes" tooltip="Required permissions for the app">
            <Select 
              mode="tags" 
              placeholder="Add scopes (auto-filled based on platform)" 
              options={[
                { label: 'pages_manage_posts', value: 'pages_manage_posts' },
                { label: 'pages_read_engagement', value: 'pages_read_engagement' },
                { label: 'instagram_basic', value: 'instagram_basic' },
                { label: 'instagram_content_publish', value: 'instagram_content_publish' },
                { label: 'w_member_social', value: 'w_member_social' },
                { label: 'tweet.read', value: 'tweet.read' },
                { label: 'tweet.write', value: 'tweet.write' },
              ]}
            />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Save</Button>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default ProviderSettingsPage;
